// Internal
import { fromJson, validateEmail, error, warn } from './helpers';

/**
 * @param {!BrickContext} $
 */
export function initCells($) {
    $.set('_rgDropdownIsOpen', '');
    $.set('_rgDropdownInputValue', '');
}

/**
 * Generate a cell for the defined wrapper/column
 * @param {!BrickContext} $
 * @param {!RGWrapper} wrapper
 * @param {number} rowId
 * @param {!Object} column
 * @param {boolean} cellEdition
 * @return {!Object}
 */
export function generateCell($, wrapper, rowId, column, cellEdition) {
    try {
        // Get the cell value
        const value = wrapper.__rgGetValue(column['Property Path'] ?? '');

        // Get the options
        const [options, optionsError] = fromJson(column.Options);
        if(optionsError) {
            warn(`cannot parse column options, error: ${optionsError.message}`);
        }
        const validator = options.validator ? s => new RegExp(options.validator).test(s) : undefined;
        const common = {
            nonEditable: cellEdition === 'DISABLED' || (cellEdition === 'USE CONFIG' && !column.Editable),
            style: getCellStyle($)
        };

        // Generate the cell depending on the type
        switch(column.Type) {
            case 'TEXT': return generateTextCell(value, options, common, column, validator);
            case 'NUMBER': return generateNumberCell(value, options, common);
            case 'EMAIL': return generateEmailCell(value, common, validator);
            case 'DATE': 
            case 'TIME': return generateDateTimeCell(value, options, common, column);
            case 'CHECKBOX': return generateCheckboxCell(value, options, common);
            case 'DROPDOWN': return generateDropdownCell(value, options, common, column, $, rowId);
            case 'HEADER': return generateHeaderCell(value, options, common);
            case 'CHEVRON': return generateChevronCell(value, options, common, wrapper, $);

            // AUTO case
            default:
                if(value instanceof Date) {
                    return generateCell($, wrapper, rowId, { ...column, Type: 'DATE' }, cellEdition);
                }
                switch(typeof value) {
                    case 'number': return generateCell($, wrapper, rowId, { ...column, Type: 'NUMBER' }, cellEdition);
                    case 'boolean': return generateCell($, wrapper, rowId, { ...column, Type: 'CHECKBOX' }, cellEdition);
                    default: return generateCell($, wrapper, rowId, { ...column, Type: 'TEXT', _cast: true }, cellEdition);
                }
        }
    }
    catch(err) {
        error(`cannot generate cell, error: ${err.message}`);
        return errorCell();
    }
}

/**
 * Get the cell value, depending on its type
 * @param {!Object} cell
 * @return {*}
 */
export function getCellValue(cell) {
    switch(cell.type) {
        case 'number': return cell.value;
        case 'date': return cell.date;
        case 'time': return cell.time;
        case 'checkbox': return cell.checked;
        case 'dropdown': return cell.selectedValue;
        default: return cell.text;
    }
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @param {!Object} column
 * @param {function(string):boolean} validator 
 * @return {!TextCell} 
 */
function generateTextCell(value, options, common, column, validator) {
    return {
        type: 'text',
        text: (column._cast ? String(value ?? '') : value ?? ''),
        placeholder: options.placeholder,
        validator: validator,
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @return {!NumberCell} 
 */
function generateNumberCell(value, options, common) {
    return {
        type: 'number',
        value: value,
        format: new Intl.NumberFormat(options.locale, options.format),
        nanToZero: options.nanToZero,
        hideZero: options.hideZero,
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} common 
 * @param {function(string):boolean} validator 
 * @return {!EmailCell} 
 */
function generateEmailCell(value, common, validator) {
    return {
        type: 'email',
        text: value ?? '',
        validator: validator ?? validateEmail,
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @param {!Object} column
 * @return {!DateCell|!TimeCell} 
 */
function generateDateTimeCell(value, options, common, column) {
    const type = column.Type.toLowerCase();
    return {
        type: type,
        [type]: value instanceof Date ? value : new Date(value),
        format: new Intl.DateTimeFormat(options.locale, options.format ?? { [`${type}Style`]: 'short' }),
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @return {!CheckboxCell} 
 */
function generateCheckboxCell(value, options, common) {
    return {
        type: 'checkbox',
        checked: Boolean(value ?? false),
        checkedText: options.checkedText,
        uncheckedText: options.uncheckedText,
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @param {!Object} column
 * @param {!BrickContext} $ 
 * @param {number} rowId
 * @return {!DropdownCell} 
 */
function generateDropdownCell(value, options, common, column, $, rowId) {
    const id = `${rowId}.${column.columnId}`;
    const isOpen = id === $.get('_rgDropdownIsOpen');
    const inputValue = isOpen ? $.get('_rgDropdownInputValue') : '';
    return {
        type: 'dropdown',
        selectedValue: value,
        values: options.values ?? [],
        isDisabled: options.isDisabled,
        isOpen: isOpen,
        inputValue: inputValue,
        ...common
    };
}

/**
 * Handle the dropdowns internal states
 * @param {!BrickContext} $ 
 * @param {!CellChange} change 
 */
export function handleDropdownCellChange($, change) {
    const id = `${change.rowId}.${change.columnId}`;
    if(change.newCell.isOpen && !change.newCell.isDisabled) {
        $.set('_rgDropdownIsOpen', id);
        $.set('_rgDropdownInputValue', change.newCell.inputValue);
    }
    else {
        $.set('_rgDropdownIsOpen', '');
        $.set('_rgDropdownInputValue', '');
    }
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @return {!HeaderCell} 
 */
function generateHeaderCell(value, options, common) {
    return {
        type: 'header',
        text: value ?? options.text ?? '',
        ...common
    };
}

/**
 * @param {*} value 
 * @param {!Object} options 
 * @param {!Object} common 
 * @param {!RGWrapper} wrapper
 * @param {!BrickContext} $ 
 * @return {!ChevronCell} 
 */
function generateChevronCell(value, options, common, wrapper, $) {
    const id = `_rgChevron.${wrapper.rgGetKey()}`;
    if(!$.has(id)) {
        $.set(id, { isExpanded: options.isExpanded ?? false });
    }
    const state = $.get(id);
    return {
        type: 'chevron',
        text: value ?? options.text ?? '',
        indent: options.indent,
        hasChildren: wrapper.rgChildren.length > 0,
        ...state,
        ...common
    };
}

/**
 * Handle the chevrons internal states
 * @param {!BrickContext} $ 
 * @param {!CellChange} change 
 * @param {!RGWrapper} wrapper 
 */
export function handleChevronCellChange($, change, wrapper) {
    const id = `_rgChevron.${wrapper.rgGetKey()}`;
    $.set(id, { isExpanded: change.newCell.isExpanded });
}

/**
 * @param {!BrickContext} $ 
 * @param {!RGWrapper} wrapper 
 * @return {boolean}
 */
export function isChevronCellExpanded($, wrapper) {
    const id = `_rgChevron.${wrapper.rgGetKey()}`;
    return $.get(id)?.isExpanded ?? false;
}

/**
 * @param {!BrickContext} $
 * @return {!Object}
 */
export function getCellStyle($) {
    const borderStyle = { 
        color: $.get('Border Color'),
        width: $.get('Border Width') 
    };
    return {
        background: $.get('Default Color'),
        border: {
            left: borderStyle,
            top: borderStyle,
            right: borderStyle,
            bottom: borderStyle
        }
    };
}

/**
 * @return {!TextCell}
 */
function errorCell() {
    return {
        type: 'text',
        text: '!ERROR',
        nonEditable: true,
        style: {
            color: 'red'
        }
    };
}
